// ─────────────────────────────────────────────────────────────────────────────
// order-source-attachment.util (WB-7399)
// ─────────────────────────────────────────────────────────────────────────────
// Picks the attachment an order's lines were extracted from, by matching each
// line's raw `source` file name against the attachment URLs.
// ─────────────────────────────────────────────────────────────────────────────

/** The only field the resolver reads off a line — structural, so IParseProduct fits as-is. */
export interface IOrderSourceLine {
  source?: string | null;
}

type SourceLines = ReadonlyArray<IOrderSourceLine | null | undefined>;
type Attachments = ReadonlyArray<string | null | undefined> | null | undefined;

/** The proxy URL's name segment is encoded once (prompt/iratt) or twice (inbox, ai/GCS). */
const MAX_DECODE_PASSES = 2;

function safeDecode(value: string): string | null {
  try {
    const decoded = decodeURIComponent(value);
    return decoded === value ? null : decoded;
  } catch {
    return null; // malformed %-escapes must never throw out of a pure resolver
  }
}

/** Decoded path without the (volatile, presigned) query string — stable identity across re-signs. */
export function attachmentPathKey(url: string | null | undefined): string {
  if (typeof url !== 'string' || !url) {
    return '';
  }
  const path = url.split('?')[0].split('#')[0];
  return safeDecode(path) ?? path;
}

/**
 * Every plausible spelling of this URL's file name, raw first then progressively decoded.
 * A candidate set rather than a single fully-decoded string: a file genuinely named
 * `50%25 off.pdf` would otherwise be over-decoded and never match.
 */
export function attachmentNameCandidates(url: string | null | undefined): string[] {
  if (typeof url !== 'string' || !url) {
    return [];
  }
  const path = url.split('?')[0].split('#')[0];
  const segment = path.split('/').filter(Boolean).pop() ?? '';
  if (!segment) {
    return [];
  }
  const candidates = [segment];
  let current = segment;
  for (let pass = 0; pass < MAX_DECODE_PASSES; pass++) {
    const decoded = safeDecode(current);
    if (decoded === null) {
      break;
    }
    if (!candidates.includes(decoded)) {
      candidates.push(decoded);
    }
    current = decoded;
  }
  return candidates;
}

/** Display name of an attachment URL — the most decoded candidate. */
export function attachmentFileName(url: string | null | undefined): string {
  const candidates = attachmentNameCandidates(url);
  return candidates.length ? candidates[candidates.length - 1] : '';
}

function normalize(value: string): string {
  return value.normalize('NFC').toLowerCase();
}

/**
 * A trailing parenthesised note, e.g. `image.png (OCR text)`. Anchored on the closing `)` at the
 * very end: anything looser (a bare word boundary after a short alnum run) also fires on the
 * version token in `PO v2.1 final.pdf`, yielding the bogus candidate `PO v2.1`.
 */
const TRAILING_NOTE = /^(.*\S)\s*\([^()]*\)$/;

/**
 * Spellings a line's `source` may take, most trustworthy first. The AI annotates how it read the
 * file — `"image.png (OCR text)"` — so the bare name is tried too, but only ever after the raw
 * value, and only when the note is genuinely parenthesised.
 */
export function sourceNameCandidates(source: string | null | undefined): string[] {
  const raw = typeof source === 'string' ? source.trim() : '';
  if (!raw) {
    return [];
  }
  const bare = raw.match(TRAILING_NOTE)?.[1]?.trim();
  return bare && bare !== raw ? [raw, bare] : [raw];
}

/**
 * Index of the first attachment the source names, or -1.
 *
 * Each candidate is tried across ALL attachments before the next one, so a derived spelling can
 * never win at one attachment while the raw name would have matched another. Exact comparison
 * first; the NFC+lowercase pass is for names that round-trip as NFD through macOS and some mail
 * clients.
 */
function indexOfAttachmentNamed(attachments: ReadonlyArray<string | null | undefined>, source: string): number {
  const wanted = sourceNameCandidates(source);
  if (!wanted.length) {
    return -1;
  }
  const offered = attachments.map((url) => attachmentNameCandidates(url));
  for (const name of wanted) {
    const exact = offered.findIndex((names) => names.includes(name));
    if (exact !== -1) {
      return exact;
    }
  }
  for (const name of wanted) {
    const wantedNormalized = normalize(name);
    const loose = offered.findIndex((names) => names.some((c) => normalize(c) === wantedNormalized));
    if (loose !== -1) {
      return loose;
    }
  }
  return -1;
}

/**
 * Whether `source` names this one attachment — the single-attachment form of `findSourceAttachment`,
 * which only answers for the order as a whole. Needed per LINE (WB-7592): an order can draw its
 * line items from two workbooks in the same email.
 */
export function sourceNamesAttachment(source: string | null | undefined, url: string | null | undefined): boolean {
  return indexOfAttachmentNamed([url], typeof source === 'string' ? source : '') === 0;
}

/**
 * The attachment the order lines were parsed from, or null when unknown/unmatched.
 *
 * Counts lines per RESOLVED attachment, so a non-file marker like `"email_body"` self-excludes
 * instead of hiding a document that really does belong to the order, and two spellings of the same
 * file (`"a.pdf"`, `"a.pdf (OCR text)"`) add up. Ties break on attachment order, which keeps the
 * result independent of line order (pagination, column sorting).
 */
export function findSourceAttachment(attachments: Attachments, lines: SourceLines): string | null {
  const list = attachments ?? [];
  if (!list.length) {
    return null;
  }
  const counts = new Map<number, number>();
  const resolved = new Map<string, number>();
  for (const line of lines ?? []) {
    const source = typeof line?.source === 'string' ? line.source.trim() : '';
    if (!source) {
      continue;
    }
    let index = resolved.get(source);
    if (index === undefined) {
      index = indexOfAttachmentNamed(list, source);
      resolved.set(source, index);
    }
    if (index !== -1) {
      counts.set(index, (counts.get(index) ?? 0) + 1);
    }
  }
  let bestIndex = -1;
  let bestCount = 0;
  counts.forEach((count, index) => {
    if (count > bestCount || (count === bestCount && index < bestIndex)) {
      bestIndex = index;
      bestCount = count;
    }
  });
  return bestIndex === -1 ? null : list[bestIndex] ?? null;
}
